import { useContext, useEffect, useRef, type ReactNode } from "react";
import { TokenContext } from "../context/TokenContext";
import { useBoundStore } from "../store";
import QueryProvider from "./QueryProvider";
import { SocketProvider } from "./socket/SocketProvider";

/** Decode the JWT payload and return the `uid` claim (Okta user ID / SSO ID). */
function extractSsoId(token: string): string {
  try {
    const payload = token.split(".")[1];
    if (!payload) return "";
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return (decoded.uid ?? decoded.sub ?? "") as string;
  } catch {
    return "";
  }
}

/**
 * Reads the token + identity from TokenContext (provided by the host shell)
 * and writes them into the Zustand shared slice so HttpService can read them
 * via useBoundStore.getState() without being coupled to React.
 *
 * When running standalone (no host shell / no TokenProvider), this is a no-op.
 */
const TokenSyncer = () => {
  const ctx = useContext(TokenContext);
  // Tracks the previous token to detect refreshes vs initial mount
  const prevTokenRef = useRef<string>("");

  useEffect(() => {
    if (!ctx) return;
    const { accessToken, residentId, facilityId, userId } = ctx;

    const isRefresh = prevTokenRef.current !== "" && prevTokenRef.current !== accessToken;
    void isRefresh; // reserved for future use (e.g. triggering side-effects on token rotation)
    prevTokenRef.current = accessToken;

    useBoundStore
      .getState()
      .setSharedData({ accessToken, residentId, facilityId, userId, ssoId: extractSsoId(accessToken) });
  }, [ctx]);

  return null;
};

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders wraps the entire application with all global context providers.
 * Add new providers here (Theme, Auth, etc.) and they will be applied app-wide.
 */
const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <QueryProvider>
      <TokenSyncer />
      <SocketProvider>
        {children}
      </SocketProvider>
    </QueryProvider>
  );
};

export default AppProviders;
